import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { crearPaymentIntent } from "../../../services/cliente/stripe";
import "./CheckoutPayment.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY, {
  locale: "en",
});

function PaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const total = sessionStorage.getItem("checkout_total");
  const [cardName, setCardName] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [status, setStatus] = useState("preparing");
  const [error, setError] = useState(null);

  const mapStripeError = useMemo(() => {
    return (err) => {
      const decline = err?.decline_code;
      const code = err?.code; 
      const key = decline || code;

      const M = {
        insufficient_funds: "Your card has insufficient funds. Please try another card.",
        lost_card: "This card was reported lost. Please use another card.",
        stolen_card: "This card was reported stolen. Please use another card.",
        do_not_honor: "Your bank declined the payment. Please try another card or contact your bank.",
        generic_decline: "Your card was declined. Please try another card.",
        fraudulent: "The payment was declined. Please try another card.",
        card_declined: "Your card was declined. Please try another card.",
        expired_card: "Your card is expired.",
        incorrect_cvc: "The security code (CVC) is incorrect.",
        incorrect_number: "The card number is incorrect.",
        invalid_number: "The card number is invalid.",
        invalid_expiry_month: "The card's expiry month is invalid.",
        invalid_expiry_year: "The card's expiry year is invalid.",
        invalid_cvc: "The card's security code (CVC) is invalid.",
        processing_error: "An error occurred while processing the card. Please try again.",
        amount_too_large: "The amount is too large to process with this card.",
        amount_too_small: "The amount is too small.",
      };
      return M[key] || "We couldn't process the payment. Please try again.";
    };
  }, []);

  useEffect(() => {
    const grupo_reserva = sessionStorage.getItem("grupo_reserva");

    if (!grupo_reserva) {
      navigate("/store/cart");
      return;
    }

    crearPaymentIntent(grupo_reserva)
      .then((res) => {
        setClientSecret(res.client_secret);
        setStatus("ready");
      })
      .catch(() => {
        setError("Unable to prepare secure payment.");
        setStatus("error");
      });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setStatus("processing");
    setError(null);

    const cardEl = elements.getElement(CardElement);

    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardEl,
        billing_details: { name: cardName },
      },
    });

    if (result.error) {
      setError(mapStripeError(result.error));
      setStatus("ready");
      return;
    }
    try {
      localStorage.removeItem("cart");
      localStorage.removeItem("art_expiration");
      localStorage.removeItem("cart_expiration");
      sessionStorage.removeItem("grupo_reserva");
      sessionStorage.removeItem("checkout_total");
    } catch {}

    navigate("/store/checkout/success", { replace: true,
      state: { payment_intent_id: result?.paymentIntent?.id },
     });
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number(value));

  if (status === "preparing") {
    return (
      <div className="adc-payment-preparing">
        <div className="adc-spinner"></div>
        <h2>Preparing secure payment</h2>
        <p>Please wait a moment…</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="adc-payment-error">
        <h2>Payment unavailable</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/store/cart")}>Return to cart</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="adc-payment-card">
      <h2>Secure Payment</h2>
      <p className="adc-payment-sub">Your payment is protected by Stripe 🔒</p>

      <div className="adc-payment-total">
        <span>Total to pay</span>
        <strong>{formatMoney(total)}</strong>
      </div>

      <input
        type="text"
        className="adc-card-name"
        placeholder="Name on card"
        value={cardName}
        onChange={(e) => setCardName(e.target.value)}
        required
      />

      <p className="adc-card-hint">Please enter the name exactly as shown on your card</p>

      <div className="adc-card-element">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#0b1f33",
                fontFamily: "Inter, system-ui, sans-serif",
                "::placeholder": { color: "#6b7c8f" },
              },
              invalid: { color: "#b42318" },
            },
          }}
        />
      </div>

      {error && <p className="adc-error">{error}</p>}

      <button className="adc-pay-btn" disabled={status === "processing"}>
        {status === "processing" ? "Processing..." : "Pay securely"}
      </button>
    </form>
  );
}

export default function CheckoutPayment() {
  return (
    <div className="adc-checkout-payment">
      <Elements stripe={stripePromise} options={{ locale: "en" }}>
        <PaymentForm />
      </Elements>
    </div>
  );
}
